/**
 * ClientNameBlock — shared display_name (prominent) + full_name (tiny) pair.
 *
 * Used on all three client-list surfaces (Agency, Coordinator, Caregiver
 * dashboards) so the display/full name pairing is defined once. Always shows
 * both names, even when identical — the disambiguation value is exactly when
 * they're similar, so hiding an identical full_name would defeat the point.
 *
 * Editing: pass `editable` plus EITHER `onEdit` (the pencil opens the parent's
 * editor — EditClientModal, which covers all four identity fields) OR `onSave`
 * (legacy inline display-name edit).
 * The edit control is a UI convenience only; the real enforcement is
 * server-side (PATCH /api/clients/:id rejects non-admins for display_name
 * regardless of whether this prop is set).
 */

import React, { useState } from "react";

export default function ClientNameBlock({
  displayName,
  fullName,
  editable = false,
  onSave,          // async (newDisplayName) => void — inline display-name edit (legacy)
  onEdit,          // () => void — when given, the pencil opens the parent's editor instead
  nameStyle,        // optional style override merged onto the default "big" name style
  fullNameStyle,    // optional style override merged onto the default "tiny" full-name style
}) {
  const [editing, setEditing] = useState(false);
  const [value,   setValue]   = useState(displayName);
  const [saving,  setSaving]  = useState(false);

  function startEdit(e) {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) { onEdit(); return; }
    setValue(displayName);
    setEditing(true);
  }

  function cancel(e) {
    e?.stopPropagation();
    setValue(displayName);
    setEditing(false);
  }

  async function commit() {
    const trimmed = value.trim();
    if (!trimmed || trimmed === displayName) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <div style={cnb.wrap} onClick={e => e.stopPropagation()}>
        <input
          autoFocus
          style={cnb.input}
          value={value}
          disabled={saving}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter")  commit();
            if (e.key === "Escape") cancel();
          }}
          onBlur={commit}
        />
        <p style={{ ...cnb.fullName, ...fullNameStyle }}>{fullName}</p>
      </div>
    );
  }

  return (
    <div style={cnb.wrap}>
      <div style={cnb.nameRow}>
        <p style={{ ...cnb.name, ...nameStyle }}>{displayName}</p>
        {editable && (
          <button
            style={cnb.editBtn}
            onClick={startEdit}
            title={onEdit ? "Edit client details" : "Edit display name"}
            aria-label={onEdit ? "Edit client details" : "Edit display name"}
          >✎</button>
        )}
      </div>
      <p style={{ ...cnb.fullName, ...fullNameStyle }}>{fullName}</p>
    </div>
  );
}

const cnb = {
  wrap:     { display: "flex", flexDirection: "column", gap: 1, minWidth: 0 },
  nameRow:  { display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
  name:     { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fullName: { fontSize: 10, color: "var(--color-text-tertiary)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  editBtn:  { fontSize: 11, background: "none", border: "none", cursor: "pointer", color: "var(--color-text-tertiary)", padding: 0, lineHeight: 1, flexShrink: 0 },
  input:    { fontSize: "var(--text-sm)", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-sm)", padding: "2px 6px", width: "100%", boxSizing: "border-box", color: "var(--color-text-primary)", background: "var(--color-bg-base)" },
};
